import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Eye, ChevronRight, RotateCcw } from "lucide-react";

import { displaySummary, privacyChanged } from "@/lib/v3Settings";
import { useMemberProfile } from "../useMemberProfile";
import { ConnectionError } from "../components/ConnectionError";
import { Loading, ConfirmDialog } from "../components/kit";
import { V3Title, V3Columns, V3Actions, AsideCard, SettingRow, V3Switch, ReadingPreview, DetailRow, DetailList, V3_PRIMARY, V3_SECONDARY } from "../components/v3";
import { SectionTitle, V3_GHOST } from "../components/v3app";

// Aside of the settings views (M25, M115): reading preview with the current display choices.
export function SettingsAside({ a11y }) {
  return (<>
    <AsideCard icon={Eye} title="Deine Lesevorschau" text="Eine gut lesbare Ansicht unterstützt deinen eigenen nächsten Schritt." testid="settings-reading" />
    <ReadingPreview large={a11y.large_text} contrast={a11y.high_contrast}
      action={<Link to="/einstellungen/darstellung" data-testid="settings-preview-next" className={`${V3_PRIMARY} lg:w-full`}>Nächsten Schritt ansehen<span className="sr-only">: Darstellung einstellen</span></Link>} />
  </>);
}

// Setting row that opens its own view; the chevron replaces the switch of the template.
export const LinkSettingRow = ({ to, title, value, testid }) => (
  <Link to={to} data-testid={testid} className="block rounded-[10px] transition-colors hover:ring-1 hover:ring-v3-magenta focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-v3-magenta">
    <SettingRow title={title} value={value} testid={testid && `${testid}-row`}><ChevronRight className="h-5 w-5 shrink-0 text-v3-muted" aria-hidden="true" /></SettingRow>
  </Link>
);

const MORE = [
  ["/einstellungen/benachrichtigungen", "Benachrichtigungen", "Arten und Kanäle einzeln wählen", "settings-more-notifications"],
  ["/einstellungen/region", "Land und Region", "Sprache, Land und Zeitzone", "settings-more-region"],
  ["/einstellungen/sicherheit", "Kontosicherheit", "E-Mail, Passwort und Sitzungen", "settings-more-security"],
  ["/einstellungen/daten", "Eigene Daten anfordern", "Kopie deiner eigenen Daten", "settings-more-export"],
  ["/einstellungen/konto-loeschen", "Konto löschen", "Zuerst die Auswirkungen prüfen", "settings-more-delete"],
];

// M25: contact requests and directory visibility are stored together via PATCH /member/profile (prefs.privacy); success only after the Core answered.
function SettingsForm({ p }) {
  const navigate = useNavigate();
  const [draft, setDraft] = useState(p.privacy);
  const [note, setNote] = useState("");
  const [confirm, setConfirm] = useState(false);
  const set = (k) => (v) => { setDraft((d) => ({ ...d, [k]: v })); setNote(""); };
  const save = async () => {
    if (!privacyChanged(draft, p.privacy)) { setNote("Es gibt keine Änderung – es wurde nichts gesendet."); return; }
    await p.savePrefs({ ...p.prefs, privacy: draft }, "Einstellungen gespeichert.");
  };
  const back = () => (window.history.length > 1 ? navigate(-1) : navigate("/profil"));
  return (
    <V3Columns layout="settings" aside={<SettingsAside a11y={p.a11y} />} main={
      <div data-m="M25" data-testid="member-settings">
        <div className="space-y-[12px]">
          <LinkSettingRow to="/einstellungen/darstellung" title="Darstellung" value={displaySummary(p.a11y)} testid="settings-display" />
          <SettingRow title="Kontaktanfragen" value={draft.allow_contact_requests ? "Von Mitgliedern erlauben" : "Nicht erlauben"} testid="settings-contact">
            <V3Switch checked={draft.allow_contact_requests} onChange={set("allow_contact_requests")} disabled={p.saving} label="Kontaktanfragen von Mitgliedern erlauben" testid="member-privacy-allow" />
          </SettingRow>
          <SettingRow title="Im Verzeichnis auffindbar" value={draft.discoverable ? "Für Mitglieder unter „Menschen“" : "Im Mitgliederverzeichnis verborgen"} testid="settings-discoverable">
            <V3Switch checked={draft.discoverable} onChange={set("discoverable")} disabled={p.saving} label="Im Mitgliederverzeichnis auffindbar" testid="member-privacy-discoverable" />
          </SettingRow>
          <SettingRow title="Ortsangabe" value="Nur Stadt · eine genaue Adresse wird nie gezeigt" testid="settings-place" />
          <LinkSettingRow to="/access-pass/freigaben" title="Access Pass" value="Privat · Eigene Freigaben verwalten" testid="settings-pass" />
        </div>
        {note && <p role="status" data-testid="settings-note" className="mt-[12px] text-[13px] font-bold text-v3-muted">{note}</p>}
        <V3Actions>
          <button type="button" onClick={save} disabled={p.saving} data-testid="settings-save" className={V3_PRIMARY}>{p.saving ? "Wird gespeichert …" : "Einstellungen speichern"}</button>
          <button type="button" onClick={back} data-testid="settings-back" className={V3_SECONDARY}>Zurück</button>
        </V3Actions>
        <button type="button" onClick={() => setConfirm(true)} disabled={p.saving} data-testid="member-acc-reset" className={`${V3_GHOST} mt-[10px] -ml-[14px]`}>
          <RotateCcw className="h-4 w-4" aria-hidden="true" />Darstellung auf Geräteeinstellungen zurücksetzen
        </button>
        <ConfirmDialog open={confirm} onOpenChange={setConfirm} testid="member-acc-reset-dialog" title="Darstellung zurücksetzen?"
          description="Grosse Schrift, Kontrast und Bewegung folgen danach wieder den Einstellungen deines Geräts." confirmLabel="Zurücksetzen"
          onConfirm={async () => { setConfirm(false); await p.resetAcc(); }} />
        <div className="mt-[30px]"><SectionTitle id="settings-more">Weitere Einstellungen</SectionTitle></div>
        <nav aria-labelledby="settings-more" className="mt-[14px]"><DetailList>
          {MORE.map(([to, label, value, testid]) => <DetailRow key={to} to={to} label={label} value={value} testid={testid} />)}
        </DetailList></nav>
      </div>
    } />
  );
}

export default function Settings() {
  const p = useMemberProfile();
  return (
    <div data-testid="settings-page">
      <V3Title title="Einstellungen und Privatsphäre" intro="Wähle Darstellung, Kontaktmöglichkeiten und Mitteilungen unabhängig voneinander." testid="settings-title" />
      {p.error ? <ConnectionError onRetry={p.load} /> : !p.data ? <Loading /> : <SettingsForm p={p} />}
    </div>
  );
}
